import {
    Alert,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type { SaveConflict } from '~/hooks/save/useSavePatientChanges';

const TITLE_ID = 'save-conflict-dialog-title';
const BODY_ID = 'save-conflict-dialog-body';

type Props = {
    conflicts: readonly SaveConflict[];
    saving: boolean;
    onOverwrite: (ids: readonly SaveConflict['id'][]) => void;
    onDiscardAndReload: () => void;
    onClose: () => void;
};

/**
 * Version-conflict resolution (SR-WOR-H-23-3). Two ways out:
 *
 * - Discard my changes and reload — the safe default (focused): the
 *   patient is closed and re-opened from the database via the standard
 *   retrieval pipeline.
 * - Overwrite the database — enabled only after an explicit
 *   acknowledgement, and never offered for a set whose server copy became
 *   APPROVED (revoking a colleague's approval is not overwritable; those
 *   sets can only be reloaded).
 *
 * Closing the dialog keeps everything pending — the indicator stays on.
 *
 * When a conflicting request had an earlier attempt fail on the network,
 * the newer server version may be the user's own interrupted write; the
 * dialog says so, and what each way out does to their changes, rather
 * than letting the conflict read as someone else's edit.
 */
export default function SaveConflictDialog({
    conflicts,
    saving,
    onOverwrite,
    onDiscardAndReload,
    onClose,
}: Props) {
    const intl = useIntl();
    const [acknowledged, setAcknowledged] = useState(false);
    const open = conflicts.length > 0;

    // A fresh conflict round requires a fresh acknowledgement. This
    // relies on the dialog CLOSING between rounds: a new save clears the
    // report first (run() → setReport(null)), so conflicts empty and
    // `open` transitions false → true, re-running this effect.
    useEffect(() => {
        if (open) {
            setAcknowledged(false);
        }
    }, [open]);

    const overwritable = conflicts.filter(
        (conflict) => conflict.reason === 'staleVersion'
    );
    const approvedOnServer = conflicts.filter(
        (conflict) => conflict.reason === 'approvedOnServer'
    );
    const afterInterruptedAttempt = overwritable.some(
        (conflict) => conflict.afterInterruptedAttempt === true
    );

    return (
        <Dialog
            open={open}
            aria-labelledby={TITLE_ID}
            aria-describedby={BODY_ID}
            // Explicit decision required: Escape is disabled and no
            // onClose is wired, so neither Escape nor a backdrop click
            // dismisses the dialog — only the buttons do (the Close
            // button keeps the changes pending).
            disableEscapeKeyDown
            fullWidth
            maxWidth="sm"
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle id={TITLE_ID}>
                {intl.formatMessage({ id: 'saveChanges.conflictTitle' })}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id={BODY_ID}>
                    {intl.formatMessage({ id: 'saveChanges.conflictBody' })}
                </DialogContentText>
                <List dense>
                    {conflicts.map((conflict) => (
                        <ListItem key={conflict.id} disableGutters>
                            <ListItemText
                                primary={conflict.displayName}
                                // Names the object kind so "approved in the
                                // database" is attributable to the right
                                // thing when a set and a registration share
                                // a name.
                                secondary={`${intl.formatMessage({
                                    id: `saveChanges.entity.${conflict.entity}`,
                                })} — ${intl.formatMessage({
                                    id:
                                        conflict.reason === 'approvedOnServer'
                                            ? 'saveChanges.conflictApproved'
                                            : 'saveChanges.conflictStale',
                                })}`}
                            />
                        </ListItem>
                    ))}
                </List>
                {afterInterruptedAttempt && (
                    <Alert
                        severity="info"
                        sx={{ mb: 1 }}
                        data-testid="interrupted-attempt-notice"
                    >
                        {intl.formatMessage({
                            id: 'saveChanges.conflictAfterInterruptedAttempt',
                        })}
                    </Alert>
                )}
                {approvedOnServer.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        {intl.formatMessage({
                            id: 'saveChanges.conflictApprovedInfo',
                        })}
                    </Alert>
                )}
                {overwritable.length > 0 && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={acknowledged}
                                onChange={(event) =>
                                    setAcknowledged(event.target.checked)
                                }
                                data-testid="overwrite-acknowledge"
                            />
                        }
                        label={intl.formatMessage({
                            id: 'saveChanges.overwriteAcknowledge',
                        })}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={saving}>
                    {intl.formatMessage({ id: 'saveChanges.conflictKeep' })}
                </Button>
                {overwritable.length > 0 && (
                    <Button
                        onClick={() =>
                            onOverwrite(overwritable.map((c) => c.id))
                        }
                        color="error"
                        disabled={!acknowledged || saving}
                        data-testid="overwrite-button"
                    >
                        {intl.formatMessage({ id: 'saveChanges.overwrite' })}
                    </Button>
                )}
                <Button
                    onClick={onDiscardAndReload}
                    variant="contained"
                    autoFocus
                    disabled={saving}
                    data-testid="discard-reload-button"
                >
                    {intl.formatMessage({ id: 'saveChanges.discardReload' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
